/**
 * Mutaciones locales del vault (zero-knowledge).
 *
 * Escriben filas OPACAS en PowerSync (SQLite local) que suben vía
 * `POST /api/sync/batch`. El servidor solo ve blobs cifrados + metadatos
 * mínimos: el cifrado/descifrado ocurre en el cliente con `@boveda/shared`
 * crypto ANTES de llamar aquí. Estas funciones nunca tocan planos.
 */

import {
  eq,
  type PowerSyncDrizzleDB,
  vault,
  vault_account,
  vault_attachment,
  vault_item,
} from "@boveda/powersync";
import type { ProtectedAccount, ProtectedAttachment, ProtectedItem } from "../crypto/types";
import { nowISO } from "../utils/date-helpers";

/** Inserta (o reemplaza) la fila raíz de cuenta del usuario. */
export async function upsertVaultAccount(
  db: PowerSyncDrizzleDB,
  account: ProtectedAccount,
  recoveryBlob?: string | null,
): Promise<void> {
  const now = nowISO();
  const existing = await db
    .select({ id: vault_account.id })
    .from(vault_account)
    .where(eq(vault_account.id, account.userId));

  const values = {
    user_id: account.userId,
    kdf: JSON.stringify(account.kdf),
    protected_account_key: account.protectedAccountKey,
    protected_private_key: account.protectedPrivateKey,
    public_key: account.publicKey,
    auth_hash: account.authHash,
    recovery_blob: recoveryBlob ?? null,
    updated_at: now,
  };

  if (existing.length > 0) {
    await db.update(vault_account).set(values).where(eq(vault_account.id, account.userId));
    return;
  }
  // PowerSync keyea por `id`; para vault_account usamos id === userId.
  await db.insert(vault_account).values({
    id: account.userId,
    created_at: now,
    ...values,
  });
}

export interface CreatePersonalVaultInput {
  id: string;
  ownerUserId: string;
  /** Nombre cifrado (envelope) — preferido — o null si se guarda en claro fuera. */
  nameEnvelope: string | null;
  /** Clave del vault envuelta con la clave de cuenta. */
  protectedKey: string;
}

/** Crea un vault PERSONAL local. */
export async function createPersonalVaultMutation(
  db: PowerSyncDrizzleDB,
  input: CreatePersonalVaultInput,
): Promise<string> {
  const now = nowISO();
  await db.insert(vault).values({
    id: input.id,
    type: "personal",
    owner_user_id: input.ownerUserId,
    organization_id: null,
    name_envelope: input.nameEnvelope,
    protected_key: input.protectedKey,
    key_epoch: 0,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  });
  return input.id;
}

/**
 * Inserta o actualiza un item cifrado. `item` ya viene de `crypto.encryptItem`
 * (sobre opaco + rev). En edición se pasa un `rev` incrementado.
 */
export async function upsertVaultItemMutation(
  db: PowerSyncDrizzleDB,
  item: ProtectedItem,
): Promise<string> {
  const now = nowISO();
  const existing = await db
    .select({ id: vault_item.id })
    .from(vault_item)
    .where(eq(vault_item.id, item.id));

  if (existing.length > 0) {
    await db
      .update(vault_item)
      .set({ envelope: item.envelope, rev: item.rev, updated_at: now, deleted_at: null })
      .where(eq(vault_item.id, item.id));
    return item.id;
  }

  await db.insert(vault_item).values({
    id: item.id,
    vault_id: item.vaultId,
    envelope: item.envelope,
    rev: item.rev,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  });
  return item.id;
}

/** Soft-delete de un item (papelera). */
export async function deleteVaultItemMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db
    .update(vault_item)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(vault_item.id, id));
}

/** Restaura un item soft-borrado. */
export async function restoreVaultItemMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db
    .update(vault_item)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(vault_item.id, id));
}

/**
 * Inserta un adjunto cifrado. `attachment` ya viene de `crypto.encryptAttachment`
 * (3 blobs opacos). El servidor solo custodia ciphertext.
 */
export async function insertVaultAttachmentMutation(
  db: PowerSyncDrizzleDB,
  attachment: ProtectedAttachment,
): Promise<string> {
  const now = nowISO();
  await db.insert(vault_attachment).values({
    id: attachment.id,
    vault_id: attachment.vaultId,
    item_id: attachment.itemId,
    wrapped_key: attachment.wrappedKey,
    meta_envelope: attachment.metaEnvelope,
    blob: attachment.blob,
    size: attachment.size,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  });
  return attachment.id;
}

/** Soft-delete de un adjunto. */
export async function deleteVaultAttachmentMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(vault_attachment)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(vault_attachment.id, id));
}
